import { useState, useEffect } from "react";
import InputField from "components/fields/InputField";
import Default from "layouts/auth/types/Default";
import { sendSignInLinkToEmail } from "@services/cloudFunctions";
import { saveEmailForSignIn } from "@services/authMessageHandler";
import notificationService from "@services/notificationService";
import firebaseEmailSignIn from "@utils/authHelper";

function SignInDefault() {
  // State for form inputs and errors
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showPasswordField, setShowPasswordField] = useState(false);

  // Countdown effect for resend button
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Handle Sign In Link Request
  const handleSignInLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    if (!email) {
      setError("Email is required");
      setLoading(false);
      return;
    }
    
    try {
      // Save email for later verification
      saveEmailForSignIn(email);
      
      // Send sign-in link
      const result = await sendSignInLinkToEmail(email);
      
      if (result.success) {
        // Show success notification
        notificationService.success(
          `Sign-in link sent to ${email}. Please check your email and click the link to continue.`,
          8000
        );
        setEmailSent(true);
        setCountdown(30); // Start 30 second countdown
      } else {
        setError(result.error || "Failed to send sign-in link");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    }
    
    setLoading(false);
  };

  // Handle legacy password sign-in
  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    if (!email || !password) {
      setError("Email and password are required");
      setLoading(false);
      return;
    }
    
    try {
      const result = await firebaseEmailSignIn(email, password, true);
      
      if (!result.success) {
        setError(result.error || "Failed to sign in");
        setLoading(false);
      }
      // If successful, the firebaseEmailSignIn function will handle the redirect
    } catch (err) {
      setError("An unexpected error occurred");
      setLoading(false);
    }
  };

  // Handle email change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };


  return (
    <Default
      maincard={
        <div className="flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:items-center lg:justify-start relative">
          
          {/* Sign in section */}
          <div className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
            <h3 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
              Sign In
            </h3>
            <p className="mb-9 ml-1 text-base text-gray-600">
              Enter your email to receive a sign-in link!
            </p>
            
            {/* Error message */}
            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
                {error}
              </div>
            )}
            
            <form onSubmit={showPasswordField ? handlePasswordSignIn : handleSignInLinkRequest}>
              {/* Email */}
              <InputField
                id="email"
                label="Email*"
                placeholder="mail@example.com"
                type="email"
                variant="auth"
                extra="mb-3"
                onChange={handleEmailChange}
              />
              
              {/* Password field - only show when enabled */}
              {showPasswordField && (
                <InputField
                  id="password"
                  label="Password*"
                  placeholder="Min. 6 characters"
                  type="password"
                  variant="auth"
                  extra="mb-3"
                  onChange={handlePasswordChange}
                />
              )}
              
              {/* Checkbox */}
              <div className="mb-4 flex items-center justify-between px-2">
                {/* <div className="mt-2 flex items-center">
                  <Checkbox 
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                  <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
                    Keep me logged In
                  </p>
                </div>
                <a
                  className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
                  href="/forgot-password"
                >
                  Forgot Password?
                </a> */}
              </div>

              {/* Send Sign-In Link Button */}
              <button 
                type="submit"
                disabled={loading || !email || (countdown > 0 && !showPasswordField) || (showPasswordField && !password)}
                className="linear w-full rounded-xl bg-brand-500 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading 
                  ? (showPasswordField ? "Signing In..." : "Sending Link...")
                  : showPasswordField
                    ? "Sign In"
                    : countdown > 0 
                      ? `Resend in ${countdown}s`
                      : emailSent 
                        ? "Resend Sign-In Link"
                        : "Send Sign-In Link"
                }
              </button>
            </form>
            
            <div className="mt-4">
              {/* <span className="text-sm font-medium text-navy-700 dark:text-gray-500">
                Not registered yet?
              </span>
              <a
                href="/auth/sign-up/default"
                className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
              >
                Create an account
              </a> */}
              <div className="mb-4 flex flex-col items-start justify-center text-sm text-gray-600 dark:text-white px-2">
              <p className="mb-0">
                Learn more at our{" "}
                <a href="https://latteconnect.com" className="text-brand-500 hover:underline">
                  Website
                </a>
                .
              </p>
              <p className="mb-0">
                Need help? Visit our{" "}
                <a href="https://latteconnect.com/support" className="text-brand-500 hover:underline">
                  Support Team
                </a>
                .
              </p>
              <p className="mb-0">
                <button
                  type="button"
                  onClick={() => setShowPasswordField(!showPasswordField)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:underline text-sm"
                >
                  {showPasswordField ? "Use email link instead" : "Use password login (legacy)"}
                </button>
              </p>
            </div>
            </div>
          </div>
        </div>
      }
    />
  );
}

export default SignInDefault;